import { getPhoneNumberCountryCodeAsNumber } from 'lib-shared-phone-numbers';
import _sortBy from 'lodash/sortBy';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import { appActions } from 'app/app.actions';
import { RootReducerStateT } from 'app/reducers/rootReducer';
import { AppDispatch } from 'bootstrap.store';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { notificationActions } from '../../../notifications/actions/notification';
import type { UserPresence } from '../../../presence/reducers/presence';
import * as telephonyActions from '../../../telephony/actions/telephony';
import { transferToAgentActions } from '../actions/transferToAgentModal';
import { TransferToAgentModalWrapper } from '../components/transferToAgentModalWrapper';

const { telephonyResetHold } = telephonyActions.actions;
const { notificationAdd, notificationRemoveByType } = notificationActions;

export type StateProps = ReturnType<typeof mapStateToProps>;
export type DispatchProps = ReturnType<typeof mapDispatchToProps>;

const getAllPresence = (state: RootReducerStateT): Array<UserPresence> =>
  state.presence.allPresence;
const getOnlinePresence = (state: RootReducerStateT) =>
  getAllPresence(state).filter(
    (presence: UserPresence) => presence.presence.status !== 'offline',
  );
const getSelectedFromNumber = (state: RootReducerStateT) =>
  state.telephony.selectedFromNumber;
const selectFromNumberCountryCode = createSelector(
  getSelectedFromNumber,
  (selectedFromNumber: ReturnType<typeof getSelectedFromNumber>) => {
    const DEFAULT_PREFIX = 45;
    if (selectedFromNumber) {
      return (
        getPhoneNumberCountryCodeAsNumber(selectedFromNumber.e164_format) ||
        DEFAULT_PREFIX
      );
    }
    return DEFAULT_PREFIX;
  },
);
const rank = {
  working: 1,
  acw: 2,
  away: 3,
  offline: 4,
  unknown: 5,
};
const selectSortedOnlinePresence = createSelector(
  getOnlinePresence,
  (allPresence: ReturnType<typeof getOnlinePresence>) => {
    return _sortBy(allPresence, [
      function (element) {
        return rank[element.presence.status];
      },
      function (element) {
        return element.user.name !== undefined && element.user.name !== null
          ? element.user.name.toLowerCase()
          : '';
      },
    ]);
  },
);

const mapStateToProps = (state: RootReducerStateT) => {
  return {
    meta: state.transferToAgent.meta,
    ui: state.transferToAgent.ui,
    metaConversation: state.transferToAgent.metaConversation,
    presenceMeta: state.presence.meta,
    profile: state.settings.profile.profile,
    onlinePresence: selectSortedOnlinePresence(state),
    presence: state.presence.presence,
    transferToQueueRequests: state.transferToQueue.requests,
    transferToAgentRequests: state.transferToAgent.transferRequests,
    numberTransferRequests: state.transferToNumber.transferRequests,
    fromNumberCountryCode: selectFromNumberCountryCode(state),
    queueId: state.transferToAgent.queueId,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    transferToAgentAssistedRequest: bindActionToPromise(
      dispatch,
      transferToAgentActions.transferToAgentAssistedRequest,
    ),
    transferToAgentBlindRequest: bindActionToPromise(
      dispatch,
      transferToAgentActions.transferToAgentBlindRequest,
    ),
    transferToAgentAssistedResolve: bindActionToPromise(
      dispatch,
      transferToAgentActions.transferToAgentAssistedResolve,
    ),

    transferToAgentModalClose: () =>
      dispatch(transferToAgentActions.transferToAgentModalClose()),
    transferRequestStop: (
      csid: StateProps['metaConversation']['conversation']['id'],
    ) => dispatch(transferToAgentActions.transferToAgentRequestStop({ csid })),
    transferToAgentShowMessageModal: () =>
      dispatch(transferToAgentActions.transferToAgentShowMessageModal()),
    transferToAgentHideMessageModal: () =>
      dispatch(transferToAgentActions.transferToAgentHideMessageModal()),

    notificationAdd: (payload: any) => dispatch(notificationAdd(payload)),
    notificationRemoveByType: (payload: any) =>
      dispatch(notificationRemoveByType(payload)),

    telephonyResetHold: () => dispatch(telephonyResetHold()),

    openQueueTransferModal: (csid: number) =>
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:conversationQueueTransfer',
            arg: { csid, isWarmTransferAvailable: true },
          },
        }),
      ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TransferToAgentModalWrapper);
